/**
 * Le tre lingue, dichiarate una volta sola, e la porta da cui il valore entra.
 *
 * ## Perché questo file esiste
 *
 * Perché l'elenco delle tre lingue era scritto **due volte**: `LinguaScritta` in
 * `format.ts` — che decide con quale locale si formattano date e numeri — e
 * `Lingua` in `apps/mobile/src/lingua/Lingua.tsx`, che decide con quale
 * dizionario si compongono le frasi. Due copie a mano dello stesso insieme
 * chiuso, in due pacchetti diversi, che nessuno confrontava: aggiungerne una
 * quarta in un posto solo avrebbe dato un'app con le parole nella lingua nuova e
 * le date in quella vecchia, senza che niente diventasse rosso. È K37 nella sua
 * forma esatta, ed è la ragione per cui `design-system.md` §15 scrive che la
 * conversione va fatta **in un punto solo**.
 *
 * Adesso il punto è questo. `format.ts` importa da qui, la app importa da qui, il
 * server importa da qui, e l'import da `besteferie` passa da `normalizzaLingua`.
 *
 * ## Perché `zh` e non `CH`
 *
 * La richiesta del 2026-08-05 diceva `CH`, che in ISO 3166 è la **Svizzera**. Chi
 * ha scritto la colonna su `besteferie` ha usato `ZH`, il codice ISO 639-1 del
 * cinese, che è quello giusto — e che è anche quello che riconoscono il telefono,
 * il browser e `Intl`. Dentro il sistema si usa `zh`.
 *
 * `ch` resta accettato in ingresso, e sta scritto negli alias qui sotto invece di
 * essere buttato: se qualcuno, un giorno, riempie quella colonna seguendo la
 * richiesta com'era scritta, il valore arriva e va capito. Rifiutarlo darebbe
 * l'italiano a chi legge cinese, in silenzio.
 */
/**
 * Le tre lingue. L'ordine non è alfabetico: è italiano per primo perché è la
 * lingua in cui le frasi si scrivono e da cui le altre due sono tradotte.
 */
export const LINGUE = ['it', 'en', 'zh'];
/**
 * L'italiano non è una delle tre traduzioni: è il contenuto minimo, e quindi il
 * ripiego. Chi arriva senza lingua prende questa, che è la lingua dell'azienda.
 *
 * `satisfies` e non `: Lingua`. L'annotazione allargherebbe il tipo di questa
 * costante da `'it'` a `Lingua`, e allora `Exclude<Lingua, typeof
 * LINGUA_PREDEFINITA>` — il modo in cui `traduzione.ts` ricava le lingue da
 * tradurre senza ricopiarle — diventerebbe `never`: l'insieme delle lingue meno
 * *tutte* le lingue. Con `satisfies` il controllo che `'it'` sia davvero una
 * delle tre resta (scriverci `'xx'` non compila), e il tipo resta il letterale.
 */
export const LINGUA_PREDEFINITA = 'it';
/** `x` è una delle tre, scritta esattamente come si scrive dentro il sistema. */
export function eLingua(x) {
    return typeof x === 'string' && LINGUE.includes(x);
}
/**
 * Quello che si accetta in ingresso oltre ai tre codici, e perché.
 *
 * È un elenco **dichiarato**, non una regola: un valore nuovo che compare nella
 * colonna non scivola dentro con una somiglianza: o sta qui con scritto perché,
 * o `normalizzaLingua` risponde `null` e chi l'ha chiamata lo registra. Su una
 * colonna `varchar(255)` senza vincoli, su un sistema che non controlliamo,
 * questa è l'unica forma che regge.
 *
 * - `ch` — la richiesta originale, che è la Svizzera. Vedi sopra.
 * - i nomi per esteso — la colonna è libera e la riempie una persona: `Italiano`
 *   scritto per esteso è la cosa più probabile che possa arrivare dopo il codice.
 * - `chinese`/`中文` — per la stessa ragione, dal verso di chi il cinese lo legge.
 */
const ALIAS = {
    ch: 'zh',
    italiano: 'it',
    italian: 'it',
    inglese: 'en',
    english: 'en',
    cinese: 'zh',
    chinese: 'zh',
    中文: 'zh',
};
/**
 * Da quello che c'è scritto nella colonna a una delle tre, o `null`.
 *
 * `null` vuol dire **non l'ho riconosciuto**, e non «italiano»: sono due cose
 * diverse e tenerle distinte è quello che permette a chi importa di contare
 * quante righe non ha capito. Che poi il non riconosciuto diventi italiano è
 * vero, ma è una decisione di chi legge, non di chi normalizza — e va presa
 * sapendo quante volte è stata presa.
 *
 * Si guarda dopo `trim` e senza distinzione di maiuscole perché il tipo a monte è
 * `varchar(255)`: `it`, ` IT `, `Italiano` devono finire tutti dove devono. Il
 * taglio su `-` e `_` serve alle etichette complete — `zh-Hans-CN`, `it_IT` —
 * che arrivano dai telefoni e dai browser e che non sono un errore: sono lo
 * stesso codice con la regione attaccata.
 */
export function normalizzaLingua(grezza) {
    if (typeof grezza !== 'string')
        return null;
    const pulita = grezza.trim().toLowerCase();
    if (pulita === '')
        return null;
    const senzaRegione = pulita.split(/[-_]/)[0] ?? pulita;
    if (eLingua(senzaRegione))
        return senzaRegione;
    return ALIAS[pulita] ?? ALIAS[senzaRegione] ?? null;
}
/**
 * Il nome di ogni lingua **nella lingua stessa**, come si deve: una tendina che
 * scrive «Cinese» a chi il cinese lo legge sta chiedendo di riconoscere una
 * parola in una lingua che non è la sua, che è esattamente il momento in cui
 * quella persona non sa dove premere.
 */
export const NOMI_LINGUA = {
    it: 'Italiano',
    en: 'English',
    zh: '中文',
};
//# sourceMappingURL=lingua.js.map